import React, { useEffect, useState } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, Body3Regular, CaptionLight, Head4 } from '../../lib/font';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import WhiteSquareButton from '../../components/Button/WhiteSquareButton';
import Grid, { GridColProps } from '../../components/Grid/Grid';
import Modal from '../../components/modal/Modal';
import dayjs from 'dayjs';
import { Ticket, ExpiryPolicy } from '@/features/groupTicket/types';
import { useCenterMemberTickets, useUpdateTicket, useDeleteTicket } from '@/features/groupTicket/hooks';
import { usePublicMemberList } from '@/features/publicMember/hooks';

const STATUS_CFG: Record<string, { label: string; bg: string; color: string }> = {
    ACTIVE: { label: '활성', bg: '#D1FAE5', color: '#065F46' },
    EXPIRED: { label: '만료', bg: '#F3F4F6', color: '#6B7280' },
    SUSPENDED: { label: '정지', bg: '#FEF3C7', color: '#92400E' },
    REFUNDED: { label: '환불', bg: '#FEE2E2', color: '#DC2626' },
};

const CLASS_TYPE_OPTIONS = [
    { value: 'GROUP', label: '그룹' },
    { value: 'PRIVATE_1ON2', label: '1:2 프라이빗' },
    { value: 'PRIVATE_1ON3', label: '1:3 프라이빗' },
];

const EXPIRY_POLICY_LABEL: Record<string, string> = { COUNT: '회차 소진 시', PERIOD: '기간 만료 시', FIRST: '둘 중 빠른 것' };

const inputStyle: React.CSSProperties = {
    border: '1px solid #E1E4E6', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none',
};

const GroupTicketDetail = ({
    ticket,
    onBack,
    createAlert,
}: {
    ticket: Ticket;
    onBack: () => void;
    createAlert: any;
}) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // 수정 폼 상태
    const [editName, setEditName] = useState(ticket.name);
    const [editPrice, setEditPrice] = useState(ticket.price);
    const [editTotalCount, setEditTotalCount] = useState<number | undefined>(ticket.totalCount);
    const [editPeriodDays, setEditPeriodDays] = useState<number | undefined>(ticket.periodDays);
    const [editExpiryPolicy, setEditExpiryPolicy] = useState<ExpiryPolicy>(ticket.expiryPolicy || 'FIRST');
    const [editClassTypes, setEditClassTypes] = useState<string[]>(ticket.allowedClassTypes);
    const [editCancellableCount, setEditCancellableCount] = useState<number | undefined>(ticket.cancellableCount);
    const [editPerSessionFee, setEditPerSessionFee] = useState<number | undefined>(ticket.perSessionFee);

    // ticket prop이 바뀌면 편집 상태 중단 + 폼 값 재동기화 (parent의 key 주입으로 remount되지만 이중 안전장치)
    useEffect(() => {
        setIsEditing(false);
        setEditName(ticket.name);
        setEditPrice(ticket.price);
        setEditTotalCount(ticket.totalCount);
        setEditPeriodDays(ticket.periodDays);
        setEditExpiryPolicy(ticket.expiryPolicy || 'FIRST');
        setEditClassTypes([...ticket.allowedClassTypes]);
        setEditCancellableCount(ticket.cancellableCount);
        setEditPerSessionFee(ticket.perSessionFee);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticket._id]);

    // 자동 계산: 가격 / 총 횟수 (PERIOD 타입 제외)
    const autoPerSessionFee = editExpiryPolicy !== 'PERIOD' && editPrice && editTotalCount && editTotalCount > 0
        ? Math.round(editPrice / editTotalCount)
        : undefined;

    const { data: centerData } = useCenterMemberTickets({ centerId });
    const { data: memberData } = usePublicMemberList({ centerId, limit: 500 });
    const updateMutation = useUpdateTicket();
    const deleteMutation = useDeleteTicket();

    // 회원 ID → 이름 매핑
    const memberMap: Record<string, string> = {};
    (memberData?.results ?? []).forEach((m: any) => {
        if (m.memberId) memberMap[m.memberId] = m.name || '';
        if (m._id) memberMap[m._id] = m.name || '';
    });

    const memberTickets = (centerData?.results ?? [])
        .filter((mt: any) => {
            const tid = typeof mt.ticketId === 'object' ? mt.ticketId?._id : mt.ticketId;
            return tid === ticket._id;
        })
        .map((mt: any) => ({
            ...mt,
            memberName: mt.memberName || memberMap[mt.memberId] || '-',
        }));

    const handleToggleActive = async () => {
        try {
            await updateMutation.mutateAsync({ centerId, ticketId: ticket._id, body: { isActive: !ticket.isActive } });
            createAlert(ticket.isActive ? '비활성화 완료' : '활성화 완료', `수강권이 ${ticket.isActive ? '비활성화' : '활성화'}되었습니다.`, false);
            onBack();
        } catch {
            createAlert('변경 실패', '상태 변경에 실패했습니다.', true);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteMutation.mutateAsync({ centerId, ticketId: ticket._id });
            createAlert('삭제 완료', '수강권 상품이 삭제되었습니다.', false);
            onBack();
        } catch {
            createAlert('삭제 실패', '발급된 수강권이 있는 경우 삭제할 수 없습니다.', true);
        }
        setShowDeleteConfirm(false);
    };

    const handleSave = async () => {
        if (!editName.trim()) { createAlert('입력 오류', '수강권 이름을 입력해주세요.', true); return; }
        if (editClassTypes.length === 0) { createAlert('입력 오류', '사용 수업 유형을 선택해주세요.', true); return; }
        if (editExpiryPolicy === 'PERIOD' && !editPerSessionFee) {
            createAlert('입력 필요', '기간제 수강권은 회당 가격을 직접 입력해주세요.', true);
            return;
        }
        try {
            await updateMutation.mutateAsync({
                centerId,
                ticketId: ticket._id,
                body: {
                    name: editName.trim(),
                    price: editPrice,
                    totalCount: editTotalCount || undefined,
                    periodDays: editPeriodDays || undefined,
                    expiryPolicy: editExpiryPolicy,
                    allowedClassTypes: editClassTypes,
                    cancellableCount: editCancellableCount || undefined,
                    perSessionFee: editPerSessionFee != null ? editPerSessionFee : autoPerSessionFee,
                },
            });
            createAlert('수정 완료', '수강권 상품이 수정되었습니다.', false);
            onBack();
        } catch {
            createAlert('수정 실패', '수강권 수정에 실패했습니다.', true);
        }
    };

    const startEdit = () => {
        setEditName(ticket.name);
        setEditPrice(ticket.price);
        setEditTotalCount(ticket.totalCount);
        setEditPeriodDays(ticket.periodDays);
        setEditExpiryPolicy(ticket.expiryPolicy || 'FIRST');
        setEditClassTypes([...ticket.allowedClassTypes]);
        setEditCancellableCount(ticket.cancellableCount);
        setEditPerSessionFee(ticket.perSessionFee);
        setIsEditing(true);
    };

    const toggleClassType = (ct: string) => {
        setEditClassTypes((prev) =>
            prev.includes(ct) ? prev.filter((t) => t !== ct) : [...prev, ct]
        );
    };

    const colDefs: GridColProps[] = [
        { field: 'memberId', headerName: '회원', width: 120, align: 'center', customFormat: (v, row) => row.memberName || v || '-' },
        { field: 'remainingCount', headerName: '잔여', width: 80, align: 'center', customFormat: (v, row) => `${v ?? 0}/${row.totalSession ?? ((row.usedCount ?? 0) + (v ?? 0))}회` },
        { field: 'startDate', headerName: '시작일', width: 110, align: 'center', type: 'date', dateFormat: 'YY.MM.DD' },
        { field: 'expiryDate', headerName: '만료일', width: 110, align: 'center', type: 'date', dateFormat: 'YY.MM.DD' },
        { field: 'totalPrice', headerName: '판매 금액', width: 100, align: 'center', customFormat: (v, row) => `${Number(v ?? row?.price ?? 0).toLocaleString()}원` },
        {
            field: 'status', headerName: '상태', width: 70, align: 'center',
            customComponent: (v) => {
                const cfg = STATUS_CFG[v] || STATUS_CFG.ACTIVE;
                return <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600 }}>{cfg.label}</span>;
            },
        },
    ];

    return (
        <Col style={{ width: 1192, alignSelf: 'center', paddingTop: 32 }}>
            {/* 헤더 */}
            <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Row style={{ alignItems: 'center', gap: 12 }}>
                    <Head4 style={{ color: colors.LAVEL_4 }}>{ticket.name}</Head4>
                    <span style={{
                        padding: '3px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                        backgroundColor: ticket.isActive ? '#D1FAE5' : '#F3F4F6',
                        color: ticket.isActive ? '#065F46' : '#6B7280',
                    }}>
                        {ticket.isActive ? '활성' : '비활성'}
                    </span>
                </Row>
                {!isEditing && (
                    <Row style={{ gap: 8 }}>
                        <ActionButton variant="primary" onClick={startEdit}>수정</ActionButton>
                        <ActionButton variant="neutral" onClick={handleToggleActive}>
                            {ticket.isActive ? '비활성화' : '활성화'}
                        </ActionButton>
                        <ActionButton variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                            삭제
                        </ActionButton>
                    </Row>
                )}
            </Row>

            {/* 수강권 정보 — 보기 모드 */}
            {!isEditing && (
                <Col style={{ gap: 12, marginBottom: 32 }}>
                    {/* 1행: 수치 정보 */}
                    <Row style={{ gap: 12 }}>
                        {[
                            { label: '총 횟수', value: ticket.totalCount ? `${ticket.totalCount}회` : '무제한' },
                            { label: '유효 기간', value: ticket.periodDays ? `${ticket.periodDays}일` : '무기한' },
                            { label: '판매 금액', value: `${ticket.price.toLocaleString()}원` },
                            { label: '회당 가격', value: (() => {
                                if (ticket.perSessionFee) return `${ticket.perSessionFee.toLocaleString()}원`;
                                if (ticket.totalCount && ticket.totalCount > 0 && ticket.expiryPolicy !== 'PERIOD') {
                                    return `${Math.round(ticket.price / ticket.totalCount).toLocaleString()}원 (자동)`;
                                }
                                return '-';
                            })() },
                            { label: '취소 허용', value: ticket.cancellableCount ? `${ticket.cancellableCount}회` : '무제한' },
                        ].map((item) => (
                            <Col key={item.label} style={{
                                flex: 1, padding: '16px 20px', gap: 6,
                                backgroundColor: '#F8FAFC', borderRadius: 10, border: '1px solid #E5E7EB',
                            }}>
                                <CaptionLight color={colors.LAVEL_1}>{item.label}</CaptionLight>
                                <Body2Regular style={{ fontWeight: 600, color: '#111827' }}>{item.value}</Body2Regular>
                            </Col>
                        ))}
                    </Row>

                    {/* 2행: 정책 / 사용 수업 (chip 표시) */}
                    <Row style={{ gap: 12, alignItems: 'stretch' }}>
                        <Col style={{
                            flex: 1, padding: '16px 20px', gap: 8,
                            backgroundColor: '#F8FAFC', borderRadius: 10, border: '1px solid #E5E7EB',
                        }}>
                            <CaptionLight color={colors.LAVEL_1}>만료 정책</CaptionLight>
                            <Body2Regular style={{ fontWeight: 600, color: '#111827' }}>
                                {EXPIRY_POLICY_LABEL[ticket.expiryPolicy || 'FIRST']}
                            </Body2Regular>
                        </Col>

                        <Col style={{
                            flex: 2, padding: '16px 20px', gap: 8,
                            backgroundColor: '#F8FAFC', borderRadius: 10, border: '1px solid #E5E7EB',
                        }}>
                            <CaptionLight color={colors.LAVEL_1}>사용 수업</CaptionLight>
                            <Row style={{ gap: 6, flexWrap: 'wrap' }}>
                                {ticket.allowedClassTypes.length === 0 ? (
                                    <Body2Regular style={{ fontWeight: 600, color: '#9CA3AF' }}>-</Body2Regular>
                                ) : ticket.allowedClassTypes.map((t) => (
                                    <span key={t} style={{
                                        padding: '4px 10px', borderRadius: 999,
                                        fontSize: 12, fontWeight: 600, lineHeight: 1.4,
                                        backgroundColor: '#ECFDF5', color: '#065F46',
                                        border: '1px solid #A7F3D0',
                                    }}>
                                        {CLASS_TYPE_OPTIONS.find((o) => o.value === t)?.label || t}
                                    </span>
                                ))}
                            </Row>
                        </Col>
                    </Row>
                </Col>
            )}

            {/* 수강권 정보 — 수정 모드 */}
            {isEditing && (
                <Col style={{ gap: 16, marginBottom: 32, padding: 24, backgroundColor: '#F9FAFB', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                    <Col style={{ gap: 6 }}>
                        <CaptionLight color={colors.LAVEL_2}>수강권 이름</CaptionLight>
                        <input style={inputStyle} value={editName} onChange={(e) => setEditName(e.target.value)} />
                    </Col>

                    <Row style={{ gap: 16 }}>
                        <Col style={{ flex: 1, gap: 6 }}>
                            <CaptionLight color={colors.LAVEL_2}>총 횟수 (선택)</CaptionLight>
                            <input style={inputStyle} type="number" value={editTotalCount ?? ''} onChange={(e) => setEditTotalCount(e.target.value ? Number(e.target.value) : undefined)} placeholder="미설정" min={1} />
                        </Col>
                        <Col style={{ flex: 1, gap: 6 }}>
                            <CaptionLight color={colors.LAVEL_2}>유효 기간 (일, 선택)</CaptionLight>
                            <input style={inputStyle} type="number" value={editPeriodDays ?? ''} onChange={(e) => setEditPeriodDays(e.target.value ? Number(e.target.value) : undefined)} placeholder="미설정" min={1} />
                        </Col>
                    </Row>

                    <Col style={{ gap: 6 }}>
                        <CaptionLight color={colors.LAVEL_2}>만료 정책</CaptionLight>
                        <Row style={{ gap: 8 }}>
                            {(['FIRST', 'COUNT', 'PERIOD'] as ExpiryPolicy[]).map((p) => {
                                const sel = editExpiryPolicy === p;
                                return (
                                    <button key={p} onClick={() => setEditExpiryPolicy(p)}
                                        style={{
                                            flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13,
                                            border: sel ? '1.5px solid #6366F1' : '1px solid #E1E4E6',
                                            backgroundColor: sel ? '#EEF2FF' : 'white',
                                            color: sel ? '#4338CA' : '#6B7280',
                                            fontWeight: sel ? 600 : 400, cursor: 'pointer',
                                        }}>
                                        {EXPIRY_POLICY_LABEL[p]}
                                    </button>
                                );
                            })}
                        </Row>
                    </Col>

                    <Col style={{ gap: 6 }}>
                        <CaptionLight color={colors.LAVEL_2}>사용 가능 수업 유형</CaptionLight>
                        <Row style={{ gap: 8 }}>
                            {CLASS_TYPE_OPTIONS.map((opt) => {
                                const sel = editClassTypes.includes(opt.value);
                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => toggleClassType(opt.value)}
                                        style={{
                                            padding: '8px 18px', borderRadius: 8, fontSize: 13,
                                            fontWeight: sel ? 600 : 400, cursor: 'pointer',
                                            border: sel ? '1.5px solid #10B981' : '1px solid #E1E4E6',
                                            backgroundColor: sel ? '#ECFDF5' : 'white',
                                            color: sel ? '#065F46' : '#6B7280',
                                        }}
                                    >
                                        {sel ? '✓ ' : ''}{opt.label}
                                    </button>
                                );
                            })}
                        </Row>
                    </Col>

                    {/* 판매 금액 · 회당 가격 — 한 묶음으로 가까이 배치 */}
                    <Col style={{
                        gap: 8, padding: '14px 16px',
                        backgroundColor: 'white', borderRadius: 10, border: '1px solid #E5E7EB',
                    }}>
                        <Row style={{ gap: 12 }}>
                            <Col style={{ flex: 1, gap: 6 }}>
                                <CaptionLight color={colors.LAVEL_2}>판매 금액 (원)</CaptionLight>
                                <input
                                    style={inputStyle}
                                    inputMode="numeric"
                                    value={editPrice ? editPrice.toLocaleString() : ''}
                                    onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setEditPrice(v ? Number(v) : 0); }}
                                    placeholder="예: 300,000"
                                />
                            </Col>
                            <Col style={{ flex: 1, gap: 6 }}>
                                <CaptionLight color={colors.LAVEL_2}>
                                    회당 가격{editExpiryPolicy === 'PERIOD' && <span style={{ color: '#EF4444', marginLeft: 4 }}>*필수</span>}
                                </CaptionLight>
                                <input
                                    style={inputStyle}
                                    inputMode="numeric"
                                    value={editPerSessionFee ? editPerSessionFee.toLocaleString() : ''}
                                    onChange={(e) => {
                                        const v = e.target.value.replace(/[^0-9]/g, '');
                                        setEditPerSessionFee(v ? Number(v) : undefined);
                                    }}
                                    placeholder={autoPerSessionFee ? `자동: ${autoPerSessionFee.toLocaleString()}원` : '직접 입력'}
                                />
                            </Col>
                        </Row>
                        {autoPerSessionFee && !editPerSessionFee && (
                            <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                                미입력 시 자동 계산: {autoPerSessionFee.toLocaleString()}원 (판매금액 / 총 횟수)
                            </span>
                        )}
                    </Col>

                    <Col style={{ gap: 6 }}>
                        <CaptionLight color={colors.LAVEL_2}>취소 허용 횟수 (비워두면 무제한)</CaptionLight>
                        <input
                            style={inputStyle}
                            type="number"
                            value={editCancellableCount ?? ''}
                            onChange={(e) => setEditCancellableCount(e.target.value ? Number(e.target.value) : undefined)}
                            placeholder="미설정 = 무제한"
                            min={1}
                        />
                    </Col>

                    <Row style={{ gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                        <ActionButton variant="neutral" onClick={() => setIsEditing(false)}>취소</ActionButton>
                        <ActionButton variant="primary" onClick={handleSave} disabled={updateMutation.isLoading}>저장</ActionButton>
                    </Row>
                </Col>
            )}

            {/* 발급 현황 */}
            <Col style={{ gap: 12, paddingTop: 8, borderTop: '1px solid #F1F5F9' }}>
                <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                    <Row style={{ alignItems: 'center', gap: 8 }}>
                        <Body2Regular style={{ fontWeight: 700, color: '#111827' }}>발급 현황</Body2Regular>
                        <span style={{
                            padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                            backgroundColor: '#F1F5F9', color: '#475569',
                        }}>
                            총 {memberTickets.length}건
                        </span>
                    </Row>
                </Row>
                <Grid
                    colDefs={colDefs}
                    rowData={memberTickets}
                    centerTopComponent="pagination"
                />
            </Col>

            {/* 삭제 확인 모달 */}
            {showDeleteConfirm && (
                <Modal setIsVisible={() => setShowDeleteConfirm(false)}>
                    <Col style={{ padding: 32, alignItems: 'center', gap: 16 }}>
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>수강권 상품 삭제</span>
                        <span style={{ fontSize: 14, color: '#6B7280', textAlign: 'center' }}>
                            <b>{ticket.name}</b>을 삭제하시겠습니까?<br />
                            발급된 수강권이 있는 경우 삭제할 수 없습니다.
                        </span>
                        <Row style={{ gap: 8 }}>
                            <ActionButton variant="neutral" onClick={() => setShowDeleteConfirm(false)}>취소</ActionButton>
                            <ActionButton variant="danger" onClick={handleDelete} disabled={deleteMutation.isLoading}>삭제</ActionButton>
                        </Row>
                    </Col>
                </Modal>
            )}
        </Col>
    );
};

/* ═══════ 통일 스타일 액션 버튼 ═══════ */
type ActionVariant = 'primary' | 'neutral' | 'danger';
const ACTION_VARIANTS: Record<ActionVariant, { bg: string; color: string; border: string; hoverBg: string }> = {
    primary: { bg: '#3B82F6', color: 'white', border: '1px solid #3B82F6', hoverBg: '#2563EB' },
    neutral: { bg: 'white', color: '#374151', border: '1px solid #D1D5DB', hoverBg: '#F9FAFB' },
    danger: { bg: 'white', color: '#DC2626', border: '1px solid #FCA5A5', hoverBg: '#FEF2F2' },
};

const ActionButton = ({
    variant, onClick, disabled, children,
}: {
    variant: ActionVariant;
    onClick?: () => void;
    disabled?: boolean;
    children: React.ReactNode;
}) => {
    const v = ACTION_VARIANTS[variant];
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = v.hoverBg; }}
            onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = v.bg; }}
            style={{
                height: 36, padding: '0 16px', minWidth: 72,
                borderRadius: 8, fontSize: 13, fontWeight: 600,
                backgroundColor: v.bg, color: v.color, border: v.border,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color 0.15s',
            }}
        >
            {children}
        </button>
    );
};

export default GroupTicketDetail;
